/**
 * DanaVoice - Toast Notification Context
 * 
 * Provides toast notifications for real-time events like new calls and appointments.
 */

import React, { createContext, useContext, useState, useCallback, ReactNode } from 'react';
import { X, Phone, Calendar, Bell, CheckCircle, AlertCircle, Info } from 'lucide-react';

export type ToastType = 'success' | 'error' | 'info' | 'warning' | 'call' | 'appointment';

export interface Toast {
  id: string;
  type: ToastType;
  title: string;
  message?: string;
  duration?: number;
  action?: {
    label: string;
    onClick: () => void;
  };
}

interface ToastContextValue {
  toasts: Toast[];
  showToast: (toast: Omit<Toast, 'id'>) => void;
  hideToast: (id: string) => void;
  showCallToast: (caller: string, phone?: string) => void;
  showAppointmentToast: (residentName: string, time: string) => void;
}

const ToastContext = createContext<ToastContextValue | undefined>(undefined);

export const useToast = () => {
  const context = useContext(ToastContext);
  if (!context) {
    throw new Error('useToast must be used within ToastProvider');
  }
  return context;
};

interface ToastProviderProps {
  children: ReactNode;
}

export const ToastProvider: React.FC<ToastProviderProps> = ({ children }) => {
  const [toasts, setToasts] = useState<Toast[]>([]);

  const showToast = useCallback((toast: Omit<Toast, 'id'>) => {
    const id = Math.random().toString(36).substr(2, 9);
    const newToast: Toast = { id, duration: 5000, ...toast };
    
    setToasts(prev => [...prev, newToast]);

    // Auto-dismiss after duration
    if (newToast.duration) {
      setTimeout(() => {
        hideToast(id);
      }, newToast.duration);
    }
  }, []);

  const hideToast = useCallback((id: string) => {
    setToasts(prev => prev.filter(t => t.id !== id));
  }, []);

  const showCallToast = useCallback((caller: string, phone?: string) => {
    showToast({
      type: 'call',
      title: 'New Call Received',
      message: `${caller}${phone ? ` (${phone})` : ''}`,
      duration: 8000,
      action: {
        label: 'View',
        onClick: () => {
          window.location.href = '/call-logs';
        }
      }
    });
  }, [showToast]);

  const showAppointmentToast = useCallback((residentName: string, time: string) => {
    showToast({
      type: 'appointment',
      title: 'New Appointment Scheduled',
      message: `Visit for ${residentName} at ${time}`,
      duration: 8000,
      action: {
        label: 'View',
        onClick: () => {
          window.location.href = '/appointments';
        }
      }
    });
  }, [showToast]);

  return (
    <ToastContext.Provider value={{ toasts, showToast, hideToast, showCallToast, showAppointmentToast }}>
      {children}
      <ToastContainer toasts={toasts} onDismiss={hideToast} />
    </ToastContext.Provider>
  );
};

interface ToastContainerProps {
  toasts: Toast[];
  onDismiss: (id: string) => void;
}

const ToastContainer: React.FC<ToastContainerProps> = ({ toasts, onDismiss }) => {
  if (toasts.length === 0) return null;

  return (
    <div className="fixed top-4 right-4 z-50 space-y-2 max-w-md">
      {toasts.map(toast => (
        <ToastItem key={toast.id} toast={toast} onDismiss={onDismiss} />
      ))}
    </div>
  );
};

interface ToastItemProps {
  toast: Toast;
  onDismiss: (id: string) => void;
}

const ToastItem: React.FC<ToastItemProps> = ({ toast, onDismiss }) => {
  const getIcon = () => {
    switch (toast.type) {
      case 'success':
        return <CheckCircle className="w-5 h-5 text-green-500" />;
      case 'error':
        return <AlertCircle className="w-5 h-5 text-red-500" />;
      case 'warning':
        return <AlertCircle className="w-5 h-5 text-yellow-500" />;
      case 'call':
        return <Phone className="w-5 h-5 text-blue-500" />;
      case 'appointment':
        return <Calendar className="w-5 h-5 text-purple-500" />;
      case 'info':
      default:
        return <Info className="w-5 h-5 text-blue-500" />;
    }
  };

  const getBgColor = () => {
    switch (toast.type) {
      case 'success':
        return 'bg-green-50 border-green-200';
      case 'error':
        return 'bg-red-50 border-red-200';
      case 'warning':
        return 'bg-yellow-50 border-yellow-200';
      case 'call':
        return 'bg-blue-50 border-blue-200';
      case 'appointment':
        return 'bg-purple-50 border-purple-200';
      case 'info':
      default:
        return 'bg-white border-gray-200';
    }
  };

  return (
    <div
      className={`${getBgColor()} border rounded-lg shadow-lg p-4 animate-slide-in-right flex items-start gap-3`}
      role="alert"
    >
      <div className="flex-shrink-0 mt-0.5">
        {getIcon()}
      </div>
      
      <div className="flex-1 min-w-0">
        <p className="text-sm font-semibold text-gray-900">
          {toast.title}
        </p>
        {toast.message && (
          <p className="mt-1 text-sm text-gray-600">
            {toast.message}
          </p>
        )}
        {toast.action && (
          <button
            onClick={() => {
              toast.action!.onClick();
              onDismiss(toast.id);
            }}
            className="mt-2 text-sm font-medium text-blue-600 hover:text-blue-800"
          >
            {toast.action.label} →
          </button>
        )}
      </div>

      <button
        onClick={() => onDismiss(toast.id)}
        className="flex-shrink-0 text-gray-400 hover:text-gray-600"
        aria-label="Dismiss notification"
      >
        <X className="w-4 h-4" />
      </button>
    </div>
  );
};